import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Roadmap {
    roadmap_id: number;
    roadmap_name: string;
    created_at: Date;
    modified_at: Date;
}

interface RoadMapState {
    data: Roadmap[],
    loading: boolean,
    loadingMore: boolean,
    hasMore: boolean,
    page: number,
    perPage: number,
    totalPages: number,
    totalItems: number,
    searchTerm: string,
    error: null | object
    members?: string[]
    skills_required?: null | object
    duration?: string
    project_costs?: Record<string, unknown>
    project_cost?: string
    CPLI?: string
    start_date?: string
    end_date?: string
    durations?: Record<string, unknown>
    duration_with_working_days?: Record<string, unknown>
    duration_with_delay?: Record<string, unknown>
    members_data?: Record<string, unknown>
    is_sim_pending?: boolean
    sim_data: Record<string, unknown>;
    skill_capacity_percent?: Record<string, unknown>
    skill_members: Record<string, unknown>;
    roadmaps?: Record<string, unknown>[]
    roadmapsError?: string | null
    roadmapsLoading?: boolean
    saveRoadmapLoading?: boolean
    saveRoadmapError?: string | null
    saveRoadmapRes?: Record<string, unknown>
    singleRoadmapLoading: boolean,
    singleRoadmapRes: Record<string, unknown>,
    singleRoadmapError: string | null,
    sim_flag: boolean,
    integration_data: Record<string, unknown>;
    show_simulation: boolean,
    share_link: boolean,
    taskAllocated: Record<string, unknown>,
    org_holidays: Record<string, unknown>,
    stats: Object,
    status: string;
    child_entity_types: Record<string, number> | null;
    entity_type: string;
    progress: number;
    // cache last combined project-level API response (contains both live and sim_data)
    projectApiPayload?: Record<string, unknown> | null
    projectApiPayloadProjectId?: string | null
    resources_used_in_tasks?: Record<string, unknown>
}

const initialState: RoadMapState = {
    data: [],
    loading: false,
    loadingMore: false,
    hasMore: true,
    page: 1,
    perPage: 10,
    totalPages: 1,
    totalItems: 0,
    searchTerm: '',
    error: null,
    members: [],
    skills_required: {},
    integration_data: null,
    skill_members: {},
    duration: '',
    duration_with_working_days: '',
    duration_with_delay: '',
    project_cost: '',
    project_costs: '',
    org_holidays: [],
    CPLI: '',
    start_date: '',
    end_date: '',
    durations: [],
    sim_data: {},
    members_data: [],
    is_sim_pending: false,
    skill_capacity_percent: {},
    singleRoadmapLoading: false,
    singleRoadmapRes: [],
    singleRoadmapError: null,
    sim_flag: false,
    show_simulation: true,
    share_link: false,
    taskAllocated: {},
    stats: {},
    status: "",
    child_entity_types: {},
    entity_type: "Work Units",
    progress: 0,
    projectApiPayload: null,
    projectApiPayloadProjectId: null,
    resources_used_in_tasks: {},
}

export const roadmapSlice = createSlice({
    name: 'roadmap',
    initialState,
    reducers: {
        setRoadmap: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.data = action.payload.Results as unknown as Roadmap[]
            state.skills_required = action.payload.skills_required as null | object
            state.skill_members = action.payload.skill_members as Record<string, unknown>
            state.duration = action.payload.duration as string
            state.duration_with_working_days = action.payload.duration_with_working_days as Record<string, unknown>
            state.duration_with_delay = action.payload.duration_with_delay as Record<string, unknown>
            state.project_cost = action.payload.project_cost as string
            state.project_costs = action.payload.project_costs as Record<string, unknown>
            state.CPLI = action.payload.CPLI as string
            state.start_date = action.payload.start_date as string
            state.end_date = action.payload.end_date as string
            state.durations = action.payload.durations as Record<string, unknown>
            state.members_data = action.payload.members_data as Record<string, unknown>
            state.is_sim_pending = action.payload.is_sim_pending as boolean
            state.skill_capacity_percent = action.payload.skill_capacity_percent as Record<string, unknown>
            state.show_simulation = action.payload.show_simulation as boolean
            state.share_link = action.payload.share_link as boolean
            state.taskAllocated = action.payload.taskAllocated as Record<string, unknown>
            state.org_holidays = action.payload.org_holidays as Record<string, unknown>
            state.stats = action.payload.stats as Object
            state.status = action.payload.status as string
            state.child_entity_types = action.payload.child_entity_types as Record<string, number> | null
            state.entity_type = action.payload.entity_type as string
            state.progress = action.payload.progress as number
            state.integration_data = action.payload.integration_data as Record<string, unknown>
            state.sim_data = action.payload.sim_data as Record<string, unknown>
            state.resources_used_in_tasks = action.payload.resources_used_in_tasks as Record<string, unknown>
        },
        setProjectApiPayload: (state, action: PayloadAction<Record<string, unknown>>) => {
            if (action && action.payload && typeof action.payload === 'object' && Object.prototype.hasOwnProperty.call(action.payload, 'payload')) {
                state.projectApiPayload = action.payload.payload as Record<string, unknown> || null;
                state.projectApiPayloadProjectId = action.payload.projectId as string || null;
            } else {
                state.projectApiPayload = action.payload || null;
            }
        },
        resetRoadmap: (state) => {
            state.data = []
            state.skills_required = null
            state.skill_members = {}
            state.duration = ""
            state.duration_with_working_days = null
            state.duration_with_delay = null
            state.project_cost = ""
            state.project_costs = {}
            state.start_date = ""
            state.end_date = ""
            state.durations = []
            state.members_data = {}
            state.is_sim_pending = false
            state.skill_capacity_percent = {}
            state.show_simulation = false
            state.share_link = false
            state.taskAllocated = {}
            state.org_holidays = []
            state.stats = {}
            state.status = ""
            state.child_entity_types = {}
            state.entity_type = ""
            state.progress = 0
            state.projectApiPayload = null
            state.projectApiPayloadProjectId = null
            state.integration_data = null
            state.sim_data = {}
            state.resources_used_in_tasks = {}
        },
        setRoadmapCPLI: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.skills_required = action.payload.skills_required as null | object
            state.duration = action.payload.duration as string
            state.duration_with_working_days = action.payload.duration_with_working_days as Record<string, unknown>
            state.duration_with_delay = action.payload.duration_with_delay as Record<string, unknown>
            state.project_cost = action.payload.project_cost as string
            state.project_costs = action.payload.project_costs as Record<string, unknown>
            state.CPLI = action.payload.CPLI as string
            state.start_date = action.payload.start_date as string
            state.end_date = action.payload.end_date as string
            state.durations = action.payload.durations as Record<string, unknown>
            state.members_data = action.payload.members_data as Record<string, unknown>
            state.is_sim_pending = action.payload.is_sim_pending as boolean
            state.skill_capacity_percent = action.payload.skill_capacity_percent as Record<string, unknown>
            state.org_holidays = action.payload.org_holidays as Record<string, unknown>
        },
        setRoadmapUpdate: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.data = action.payload.Results as unknown as Roadmap[]
            state.duration = action.payload.duration as string
        },
        setProjectMembers: (state, action: PayloadAction<string[]>) => {
            state.members = action.payload
        },
        setRoadmapLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        setRoadmapError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
            state.data = []
        },
        setRoadmaps: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.roadmaps = (action.payload?.roadmaps as Record<string, unknown>[]) || []
            state.totalItems = (action.payload?.total as number) || 0
            state.totalPages = (action.payload?.pages as number) || 1
            state.page = (action.payload?.page as number) || 1
            state.perPage = (action.payload?.per_page as number) || 10
            state.hasMore = state.page < state.totalPages
        },
        appendRoadmaps: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.roadmaps = [...(state.roadmaps || []), ...((action.payload?.roadmaps as Record<string, unknown>[]) || [])]
            state.totalItems = (action.payload?.total as number) || 0
            state.totalPages = (action.payload?.pages as number) || 1
            state.page = (action.payload?.page as number) || 1
            state.perPage = (action.payload?.per_page as number) || 10
            state.hasMore = state.page < state.totalPages
        },
        setRoadmapsLoading: (state, action: PayloadAction<boolean>) => {
            state.roadmapsLoading = action.payload
        },
        setRoadmapsLoadingMore: (state, action: PayloadAction<boolean>) => {
            state.loadingMore = action.payload
        },
        setRoadmapsError: (state, action: PayloadAction<string | null>) => {
            state.roadmapsError = action.payload
            state.data = []
        },
        resetRoadmapsPagination: (state) => {
            state.page = 1
            state.hasMore = true
        },
        setRoadmapSearchTerm: (state, action: PayloadAction<string>) => {
            state.searchTerm = action.payload
            state.page = 1
            state.hasMore = true
        },
        setSaveRoadmap: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.saveRoadmapRes = action.payload
        },
        setSaveRoadmapLoading: (state, action: PayloadAction<boolean>) => {
            state.saveRoadmapLoading = action.payload
        },
        setSaveRoadmapError: (state, action: PayloadAction<string | null>) => {
            state.saveRoadmapError = action.payload
            state.saveRoadmapRes = [] as unknown as Record<string, unknown>
        },
        setSingleRoadmap: (state, action: PayloadAction<Record<string, unknown>>) => {
            state.singleRoadmapRes = action.payload
            state.is_sim_pending = (action.payload as Record<string, unknown>)?.is_sim_pending as boolean ?? false
        },
        setSingleRoadmapLoading: (state, action: PayloadAction<boolean>) => {
            state.singleRoadmapLoading = action.payload
        },
        setSingleRoadmapError: (state, action: PayloadAction<string | null>) => {
            state.singleRoadmapError = action.payload
            state.saveRoadmapRes = [] as unknown as Record<string, unknown>
        },
        setSimulationToggle: (state, action: PayloadAction<boolean>) => {
            state.sim_flag = action.payload
        }
    }
});

export const { setRoadmap, resetRoadmap, setRoadmapUpdate,
    setRoadmapLoading, setRoadmapError, setProjectMembers, setRoadmapCPLI,
    setRoadmapsLoading, setRoadmaps, setRoadmapsError, setSaveRoadmapLoading,
    setSaveRoadmapError, setSaveRoadmap, setSingleRoadmapLoading, setSingleRoadmap,
    setSingleRoadmapError, setSimulationToggle, setProjectApiPayload,
    appendRoadmaps, setRoadmapsLoadingMore, resetRoadmapsPagination, setRoadmapSearchTerm
} = roadmapSlice.actions;

export default roadmapSlice.reducer;
